import { useEffect, useRef, useState } from "react";
import { Employee, WorkTask } from "../../types/ganttChart";
import {
  addFilter,
  addItemToSearchHistory,
  clearFilters,
  clearHistory,
  SearchItem,
  setHighlightedTasks,
} from "../../store/search";
import searchStyles from "../../styles/search.module.css";
import labels from "../../utils/labels";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import SearchResult from "./SearchResult";
import SearchIndex from "../../service/SearchIndex";
import SearchBar from "./SearchBar";
import SearchItemChip from "./SearchItemChip";

const GanttChartSearchBar = ({
  employees,
  unplannedTasks,
}: {
  employees: Employee[];
  unplannedTasks: WorkTask[];
}) => {
  const [input, setInput] = useState("");
  const [suggestions, setSuggestions] = useState<SearchItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);
  const dispatch = useAppDispatch();
  const { appliedFilters, history } = useAppSelector(
    (state) => state.searchSlice,
  );

  const searchIndex = new SearchIndex([
    ...employees.flatMap((employee) => employee.tasks.workTasks),
    ...unplannedTasks,
  ]);

  const fetchSuggestions = () => {
    if (input.length > 0) {
      const results = searchIndex.suggest(input, appliedFilters);
      setSuggestions(results);
      setIsOpen(results.length > 0);
    } else {
      setSuggestions([]);
      setIsOpen(false);
    }
  };

  useEffect(() => {
    fetchSuggestions();
  }, [input]);

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Enter") {
      const highlighted = history.flatMap((item) => item.ids);
      dispatch(setHighlightedTasks(highlighted));
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      // document.removeEventListener("mousedown", handleClickOutside); - doublecheck
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const handleSuggestionClick = (suggestion: SearchItem) => {
    dispatch(setHighlightedTasks(suggestion.ids));
    dispatch(addFilter(suggestion));
    dispatch(addItemToSearchHistory(suggestion));
    setInput("");
    setIsOpen(false);
  };

  const handleClearAll = () => {
    setInput("");
    dispatch(clearFilters());
    dispatch(clearHistory());
    dispatch(setHighlightedTasks([]));
  };

  const chips = appliedFilters.map((searchItem, index) => (
    <SearchItemChip searchResult={searchItem} key={index} />
  ));

  return (
    <div className="relative" style={{ maxWidth: 640, width: "100%" }}>
      <div ref={searchRef}>
        {/* search bar */}
        <SearchBar
          input={input}
          onInputChange={setInput}
          appliedFilters={chips}
          onClearAll={handleClearAll}
          placeholder={labels.searchPrompt}
        />
        {/* search dropdown */}
        {isOpen && suggestions.length > 0 && (
          <ul
            ref={dropdownRef}
            className={searchStyles.searchSugeestionsWrapper}
          >
            <div className="flex w-[100%]">
              <p className="[font:var(--font-label)] text-[var(--col-text-secondary)]">
                {labels.results}
              </p>
            </div>
            {suggestions.map((suggestion, index) => (
              <SearchResult
                key={index}
                result={suggestion}
                onClick={() => handleSuggestionClick(suggestion)}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default GanttChartSearchBar;
